import { useRef, useEffect, useState, useMemo, useId, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { observer } from 'mobx-react-lite'
import { useNavigate, useParams } from 'react-router'

// evertel
import { MessageWall, MessageSearchModal } from '@evertel/web/message'
import { DocumentModalContextProvider } from '@evertel/web/document'
import {
    RoomHeader,
    RightGutter,
    RoomSettings,
    RoomMediaSearch,
    RoomDirectory,
    RoomReplies,
    RoomConnectors
} from './elements'
import {
    PhotoGallery, Spinner, SlidePanel, SlidePanelHeader,
    Row, SlidePanelBody, Badge, InfoBox, Col, HiddenContent,
    Button, Icon, useUI
} from '@evertel/web/ui'
import { useService } from '@evertel/di'
import { RoomController, RoomConnectorsController } from '@evertel/room'
import { SessionService } from '@evertel/session/SessionService'
import { formatNumber } from '@evertel/utils'
import { CurrentUserController, UserSettingsController } from '@evertel/blue-user'
import { DrawerController, RepliesViewController } from '@evertel/message'
import { RoomContext } from './RoomContext'
import { RoomGate } from './RoomGate'
import { RoomJoinRequestsController } from '@evertel/room'
import { useDocumentTitleSuffix } from '@evertel/web/core'

const Room = observer(() => {

    const { id: currentUrlIdString, repliesToId: repliesToIdString } = useParams<{ id: string, repliesToId?: string }>()
    const currentRoomId = currentUrlIdString ? parseInt(currentUrlIdString, 10) : null
    const repliesToId = repliesToIdString ? parseInt(repliesToIdString, 10) : null

    const sessionService = useService(SessionService, [])

    const userSettingsController = useService(UserSettingsController, [])
    const currentUserController = useService(CurrentUserController, [])

    const drawerController = useService(DrawerController, [sessionService.selectedDepartmentId])
    const roomController = useService(RoomController, [currentRoomId])
    const roomConnectorsController = useService(RoomConnectorsController, [currentRoomId])
    const roomJoinRequestsController = useService(RoomJoinRequestsController, [currentRoomId])
    const repliesViewController = useService(RepliesViewController, [currentRoomId])

    const { addToast } = useUI()
    const { setTitleSuffix } = useDocumentTitleSuffix()

    const navigate = useNavigate()

    const [gatesComplete, setGatesComplete] = useState(!currentRoomId)
    const [isLoading, setIsLoading] = useState(false)
    const [showAsideTab, setShowAsideTab] = useState(null)
    const [openAside, setOpenAside] = useState(false)
    const [asideOpenComplete, setAsideOpenComplete] = useState(false)
    const [isMessageSearchModalOpen, setIsMessageSearchModalOpen] = useState(false)
    const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(null)
    const [asideProps, setAsideProps] = useState({})
    const [slideDirection, setSlideDirection] = useState<'left' | 'right' | null>(null)

    const initializationAttempted = useRef(false)
    const hasCorrectedDepartment = useRef(false)

    const handleGatesComplete = useCallback(() => setGatesComplete(true), [])

    useEffect(() => {
        if (!currentRoomId) {
            addToast({
                color: 'danger',
                message: 'No room specified.'
            })

            navigate('/landing', { replace: true })
        }
        initializationAttempted.current = false
    }, [currentRoomId, sessionService.selectedDepartmentId])

    useEffect(() => {
        setGatesComplete(false)
    }, [currentRoomId])

    // Set document title suffix
    useEffect(() => {
        if (roomController.room?.id) {
            const suffix = repliesToId
                ? `#${roomController.room.id} - Reply #${repliesToId}`
                : `#${roomController.room.id}`
            setTitleSuffix(suffix)
        }
    }, [roomController.room?.id, repliesToId, setTitleSuffix])

    const asideTabs = useMemo(() => ({
        directory: {
            title: 'Room Members',
            component: RoomDirectory
        },
        replies: {
            title: 'Replies',
            component: RoomReplies,
            markAll: async () => await repliesViewController.markAllRepliesAsRead(currentRoomId)
        },
        settings: {
            title: 'Room Settings',
            component: RoomSettings
        },
        connectors: {
            title: 'Manage Connectors',
            component: RoomConnectors
        },
        'media-search': {
            title: 'File Search',
            component: ({ initialTab }) => <RoomMediaSearch initialTab={initialTab} />
        },
        closed: {
            title: null,
            component: () => null
        }
    }), [currentRoomId])

    const handleCloseSlidePanel = () => {
        setAsideOpenComplete(false)
        toggleAside(showAsideTab, 'close')
    }

    if (currentRoomId && !roomController.roomId && !initializationAttempted.current) {
        //Goal: initialize roomController as soon as possible before first render
        initializationAttempted.current = true
        setIsLoading(true)
        roomController.init(currentRoomId, repliesToId)
        roomController.fetchRoomDetails().catch((error) => {
            setIsLoading(false)
            console.error('Room init', error.message)
        })
    }

    useEffect(() => {
        // Reset when room changes, otherwise, if page loaded in room,
        // switching departments fails the first time if no other navigation happened.
        hasCorrectedDepartment.current = false
    }, [currentRoomId])

    useEffect(() => {
        if (roomController.room && !hasCorrectedDepartment.current) {
            hasCorrectedDepartment.current = true

            const departmentId = roomController.room.departmentId
            const isInterAgencyRoom = drawerController.interAgencyRooms.some(room => departmentId === room.departmentId)

            //update the selectedDepartmentId if a room is entered that doesn't match the current department
            if (departmentId && departmentId !== sessionService.selectedDepartmentId && !isInterAgencyRoom) {
                const previousDeptId = sessionService.selectedDepartmentId
                sessionService.setSelectedDeparmentId(departmentId)

                // Clean up this room from the previous department's history - it was stored there incorrectly
                sessionService.deleteRoomFromNavigationHistory(currentRoomId, previousDeptId)

                // useEffect will be triggered again after this, so no point in running init requests
                //     and we'd want to keep isLoading true for now
                return
            }

            userSettingsController.fetchUserSettings().catch(() => { /** noop */ })

            setIsLoading(false)
        }
    }, [roomController.room, sessionService.selectedDepartmentId])

    useEffect(() => {
        roomController.repliesToId = repliesToId

        if (repliesToId) {
            roomController.fetchRepliesToMessage()
            roomController.startPoll()
        }

        return () => {
            roomController.stopPoll()
        }
    }, [repliesToId])

    useEffect(() => {
        const canManageRequests = roomController?.canCurrentUserManageRoom && currentUserController.isMemberInDepartment(roomController.room?.departmentId)
        if (canManageRequests) {
            roomJoinRequestsController.init(currentRoomId)
        }
    }, [roomController.roomId, roomController.managerIds])

    useEffect(() => {
        if (roomController.room?.id && roomController.room?.departmentId && roomController.room?.integrationAcl) {
            roomConnectorsController.init(
                roomController.room.id,
                roomController.room.departmentId,
                roomController.room.integrationAcl
            )
            if (roomConnectorsController.hasActiveConnectors) {
                roomConnectorsController.fetchDepartmentIntegrations()
            }
        }
    }, [roomController.room?.id, roomController.room?.departmentId, roomController.room?.integrationAcl])

    useEffect(() => {
        const container = document.getElementById('slide-panel-root')
        if (container) setPortalContainer(container)

        return () => { setPortalContainer(null) }
    }, [])

    const toggleAside = (tab = 'directory', intent: 'open' | 'close', props = {}) => {
        if (intent === 'close') {
            setSlideDirection(null)
            setOpenAside(false)
            setShowAsideTab(null)
            setAsideProps({})
        } else if (intent === 'open') {
            // Track slide direction for settings ↔ connectors transitions
            if (showAsideTab === 'settings' && tab === 'connectors') {
                setSlideDirection('left')
            } else if (showAsideTab === 'connectors' && tab === 'settings') {
                setSlideDirection('right')
            } else {
                setSlideDirection(null)
            }
            setShowAsideTab(tab)
            setOpenAside(true)
            setAsideProps(props)
        }
    }

    const contextValues = {
        canManageRoom: roomController?.canCurrentUserManageRoom,
        roomController,
        roomConnectorsController,
        userSettingsController,
        roomJoinRequestsController,
        setIsMessageSearchModalOpen,
        toggleAside,
        asideOpenComplete
    }

    const AsideComponent = asideTabs[showAsideTab]?.component || asideTabs.closed.component

    if (!gatesComplete) {
        return (
            <div className="page-body animated">
                <RoomGate
                    roomId={currentRoomId}
                    onComplete={handleGatesComplete}
                />
            </div>
        )
    }

    return (
        <RoomContext.Provider value={contextValues}>
            <PhotoGallery>
                <DocumentModalContextProvider>
                    <Row valign="stretch" className="room-container h-100">
                        <Col valign="start" className="w-100">
                            <RoomHeader />

                            {!roomController.room && (
                                <InfoBox
                                    color="warning"
                                    className="m-3"
                                >
                                    Sorry, this room was not found or you do not have access to it
                                </InfoBox>
                            )}
                            <HiddenContent visible={!!roomController.room}>
                                <MessageWall
                                    modelId={roomController.roomId}
                                    modelType="room"
                                    modelData={roomController.room}
                                    allowedToPost={roomController.allowedToPost}
                                    repliesToMessage={roomController.repliesToMessage}
                                />
                            </HiddenContent>

                        </Col>
                        <HiddenContent visible={!!roomController.room}>
                            <RightGutter
                                activeTab={isMessageSearchModalOpen ? 'search' : (openAside && showAsideTab)}
                                toggleAside={toggleAside}
                            />
                        </HiddenContent>
                    </Row>

                    <MessageSearchModal
                        visible={isMessageSearchModalOpen}
                        onClose={() => setIsMessageSearchModalOpen(false)}
                        modelType="room"
                        modelId={roomController.room?.id}
                        modelName={roomController.room?.name}
                    />

                    {portalContainer && createPortal(
                        <SlidePanel
                            id={`slidepanel-${showAsideTab}`} //must match gutter link id
                            className="room-aside"
                            visible={openAside}
                            onHide={() => handleCloseSlidePanel()}
                            onShowComplete={() => setAsideOpenComplete(true)}
                            backdrop={false}
                            placement="end"
                        >
                            <SlidePanelHeader
                                title={(
                                    <>
                                        {showAsideTab === 'connectors' && (
                                            <Button
                                                color="link"
                                                className="px-1 me-2 mb-1"
                                                onClick={() => toggleAside('settings', 'open')}
                                            >
                                                <Icon name="arrow-left" type="solid" />
                                            </Button>
                                        )}
                                        <span>{asideTabs[showAsideTab]?.title}</span>
                                        {(showAsideTab === 'directory') && (
                                            <Badge color="muted" className="ms-2">
                                                {formatNumber(roomController.usersCount)}
                                            </Badge>
                                        )}
                                    </>
                                )}
                                actions={
                                    asideTabs[showAsideTab]?.markAll && (
                                        <Button
                                            className="me-2"
                                            color="link"
                                            onClick={asideTabs[showAsideTab]?.markAll}
                                            disabled={repliesViewController.isMarkingUnreads}
                                        >
                                            <span className="position-relative d-inline-flex align-items-center w-30px h-100 me-1 align-middle">
                                                {repliesViewController.isMarkingUnreads && <Spinner width={30} />}
                                            </span>
                                            <span className="align-middle">Mark all as read</span>
                                        </Button>
                                    )
                                }
                            >
                            </SlidePanelHeader>
                            <SlidePanelBody>
                                <div
                                    key={showAsideTab}
                                    className={slideDirection ? `slide-in-${slideDirection}` : undefined}
                                >
                                    <AsideComponent {...asideProps}/>
                                </div>
                            </SlidePanelBody>
                        </SlidePanel>,
                        portalContainer
                    )}

                </DocumentModalContextProvider>
            </PhotoGallery>
        </RoomContext.Provider>
    )
})

Room.displayName = 'Room'

export { Room }
